/*
 * gh-rtl.js — Arabic (dir="rtl") runtime fixes.
 *
 * Loaded from layout/theme.liquid ONLY when request.locale.iso_code == 'ar',
 * immediately after slick.min.js and before the body's own inline scripts, and
 * it bails out at once unless the document direction is rtl. It cannot run on
 * the English or French site.
 *
 * Four things CSS cannot do:
 *
 *  0. Tell gh-rtl.css which page-level stylesheets are on the page
 *     (html.ghs-<name>), so the RTL copy of a section's sheet only applies
 *     where that sheet does.
 *
 *  1. Slick carousels need `rtl: true` passed at init. The theme initialises
 *     Slick in six section liquid files that are out of scope for this change,
 *     so $.fn.slick is wrapped here instead and the flag is injected into every
 *     options object. slick.css / slick-theme.css already ship their own
 *     [dir='rtl'] rules, so no CSS work is needed for them.
 *
 *  2. Dawn's SliderComponent measures slides with offsetLeft/scrollLeft. In RTL
 *     the item offset is negative, which (correctly, by double negative) keeps
 *     the prev/next buttons scrolling the right way, but makes slidesPerPage
 *     and therefore the "n / N" counter total negative-derived and wrong.
 *
 *  3. menu-weight-motion.js splits every desktop menu label into one
 *     inline-block cell per letter, which cannot join Arabic letters and reads
 *     a Latin label backwards in an RTL line; labels are kept whole instead.
 *
 * Swiper needs nothing: it reads the container's computed direction itself and
 * adds its own .swiper-rtl class.
 */
(function () {
  'use strict';

  if (document.documentElement.getAttribute('dir') !== 'rtl') return;

  /* ---- 0. Page gate for gh-rtl.css --------------------------------------
     The RTL copy of a page-level stylesheet (a section's component-*.css,
     section-*.css, ...) must only apply on pages that load that sheet, or its
     generic rules leak onto every page. gh-rtl.css keys those rules on
     html.ghs-<name>; this puts the class on <html> for every
     /assets/<name>.css link in the document, including links the parser meets
     later in the body and links sections insert at run time (quick add). The
     observer fires before the next frame, so there is no visible flip. */
  var root = document.documentElement;
  function markSheet(link) {
    var m = /\/assets\/([\w.-]+)\.css(?:[?#]|$)/.exec(link.getAttribute('href') || '');
    if (m) root.classList.add('ghs-' + m[1].replace(/[^\w-]/g, '-'));
  }
  try {
    Array.prototype.forEach.call(document.querySelectorAll('link[href*="/assets/"]'), markSheet);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'LINK') markSheet(n);
          else if (n.firstElementChild) Array.prototype.forEach.call(n.querySelectorAll('link[href*="/assets/"]'), markSheet);
        }
      }
    }).observe(root, { childList: true, subtree: true });
  } catch (e) {
    /* never let this break the page */
  }

  /* ---- 1. Slick: inject rtl:true on every init ---------------------------- */
  try {
    var $ = window.jQuery;
    if ($ && $.fn && typeof $.fn.slick === 'function') {
      var originalSlick = $.fn.slick;
      var wrapped = function () {
        var args = Array.prototype.slice.call(arguments);
        var first = args[0];
        var isOptions =
          first &&
          typeof first === 'object' &&
          !Array.isArray(first) &&
          typeof first.nodeType === 'undefined';
        if (isOptions && typeof first.rtl === 'undefined') {
          // Copy rather than mutate: several sections reuse one options object.
          args[0] = Object.assign({}, first, { rtl: true });
        }
        return originalSlick.apply(this, args);
      };
      // Keep anything Slick hung off the plugin function itself.
      Object.keys(originalSlick).forEach(function (k) {
        wrapped[k] = originalSlick[k];
      });
      $.fn.slick = wrapped;
    }
  } catch (e) {
    /* never let this break the page */
  }

  /* ---- 2. Dawn SliderComponent page maths -------------------------------- */
  function patchSliderComponent() {
    try {
      var ctor = window.customElements && window.customElements.get('slider-component');
      if (!ctor || ctor.prototype.__ghRtlPatched) return;
      var proto = ctor.prototype;
      var originalInitPages = proto.initPages;
      if (typeof originalInitPages !== 'function') return;
      proto.initPages = function () {
        originalInitPages.call(this);
        // sliderItemOffset is negative in RTL; the scroll maths relies on that
        // sign, the page count does not.
        if (typeof this.sliderItemOffset === 'number' && this.sliderItemOffset < 0) {
          this.slidesPerPage = Math.floor(
            this.slider.clientWidth / Math.abs(this.sliderItemOffset)
          );
          this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        }
      };
      proto.__ghRtlPatched = true;
    } catch (e) {
      /* never let this break the page */
    }
  }

  // global.js is deferred, so it has defined the element by DOMContentLoaded.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchSliderComponent, { once: true });
  } else {
    patchSliderComponent();
  }

  /* ---- 3. Desktop menu labels stay whole words --------------------------
     assets/menu-weight-motion.js (deferred) rebuilds every desktop menu label
     as one inline-block cell per letter for its hover cascade. In Arabic that
     is wrong twice over: isolated cells cannot join, so every Arabic letter
     would render in its detached form, and inline-block cells are bidi
     neutrals, so a Latin label reads backwards in an RTL line ("STNEVE &
     SWEN"). So, before the deferred scripts run, each label is given the
     i.wl box that script looks for, holding the text whole; the script then
     skips it (it ignores spans that already hold an i.wl). The hover state is
     a plain weight change, width-locked by gh-rtl.css. The selector list is
     the LABELS constant of menu-weight-motion.js and must stay in step. */
  var MENU_LABELS = '.header__menu-item a > span, a.header__menu-item > span, li.ctm-tab-menu a > span, ' +
    '.ctm-tab-content li.swiper-slide a.mega-menu__link > span';
  function keepMenuLabelsWhole() {
    try {
      document.querySelectorAll('nav.header__inline-menu').forEach(function (nav) {
        nav.querySelectorAll(MENU_LABELS).forEach(function (span) {
          if (span.querySelector(':scope > i.wl')) return;
          var text = span.textContent.replace(/\s+/g, ' ').trim();
          if (!text) return;
          var wl = document.createElement('i');
          wl.className = 'wl gh-rtl-whole';
          wl.setAttribute('data-text', text);
          wl.textContent = text;
          span.textContent = '';
          span.appendChild(wl);
        });
      });
    } catch (e) {
      /* never let this break the page */
    }
  }
  // 'interactive' is reached when parsing ends and BEFORE deferred scripts run.
  if (document.readyState === 'loading') {
    document.addEventListener('readystatechange', function onRs() {
      if (document.readyState !== 'loading') {
        document.removeEventListener('readystatechange', onRs);
        keepMenuLabelsWhole();
      }
    });
  } else {
    keepMenuLabelsWhole();
  }
})();
